/**
 * Converting the audio clock to `performance.now()`.
 *
 * Rule 1 of the architecture section says one clock, `performance.now()`, for
 * everything. The audio thread does not have it. It has `currentFrame`, an
 * integer counted by the sound card, which drifts against the system clock by
 * tens of parts per million because they are two different crystals.
 *
 * The naive fix — anchor once at startup and extrapolate — is wrong twice. It
 * accumulates the drift, and it bakes in whatever the postMessage latency
 * happened to be for the very first batch.
 *
 * What this does instead is the standard minimum-latency estimator. Every batch
 * arrives with the frame index it was captured at, and with a main-thread
 * arrival time that is always **later** than the true corresponding moment: the
 * quantum had to finish, the message had to cross threads, and the main thread
 * had to be free to run the handler. So each observation is an over-estimate of
 * the offset, and the *smallest* over-estimate in a recent window is the best
 * available reading — one message that happened not to be delayed calibrates the
 * whole window.
 *
 * Taking a minimum over a sliding window, rather than over all time, is what
 * lets it track drift: an offset measured a minute ago stops being evidence.
 *
 * What it deliberately does not know: the hardware input latency between sound
 * arriving at the microphone and samples reaching the worklet. That is not
 * observable from inside the page. It is also roughly constant, which means it
 * belongs in `config.latency.*` with the other per-detector corrections rather
 * than being guessed at here.
 */

export class AudioClock {
  /**
   * @param {object} o
   * @param {number} o.sampleRate  the AudioContext's, in Hz
   * @param {number} [o.window]    observations kept for the minimum
   */
  constructor({ sampleRate, window = 128 }) {
    if (!(sampleRate > 0)) throw new RangeError(`sampleRate must be positive, got ${sampleRate}`);
    this.sampleRate = sampleRate;
    this.window = Math.max(1, Math.floor(window));
    /** Recent offset observations, oldest first. @type {number[]} */
    this._obs = [];
    /** Current best estimate of `perf - frameTimeMs`. @type {?number} */
    this.offset = null;
  }

  get ready() {
    return this.offset !== null;
  }

  /** Audio-clock milliseconds for a frame index. */
  frameMs(frame) {
    return (frame / this.sampleRate) * 1000;
  }

  /**
   * Fold in one batch arrival.
   *
   * @param {number} frame  frame index the batch started at
   * @param {number} arrivedAt  performance.now() in the message handler
   * @returns {number} the offset now in force
   */
  observe(frame, arrivedAt) {
    const observed = arrivedAt - this.frameMs(frame);
    this._obs.push(observed);
    if (this._obs.length > this.window) this._obs.shift();

    let min = this._obs[0];
    for (let i = 1; i < this._obs.length; i++) {
      if (this._obs[i] < min) min = this._obs[i];
    }
    this.offset = min;
    return min;
  }

  /**
   * `performance.now()` for a frame index.
   *
   * Returns null until the first observation, rather than a plausible zero. A
   * timestamp that is silently an hour out is worse than no timestamp: it lands
   * a sample in the ring buffer where a binary search will happily find it.
   *
   * @param {number} frame
   * @returns {?number}
   */
  toPerformanceTime(frame) {
    if (this.offset === null) return null;
    return this.frameMs(frame) + this.offset;
  }

  reset() {
    this._obs = [];
    this.offset = null;
  }
}

export default AudioClock;
